import axios from 'axios';
import { useState } from 'react';

import { pauseSurvey } from '@api/surveys';
import { Announcement, Button, ModalV2 as Modal, Select } from '@utilities';

const possibleReasons = [
  { label: 'Programming concern (possible missed questions, logic, etc.)', value: 'concern' },
  { label: 'To change a question(s) in the survey', value: 'change_question' },
  { label: 'To change an answer option(s) in the survey', value: 'change_answer' },
  { label: 'Made a mistake on sample selection', value: 'sample_selection' },
  { label: 'Disqualification rate seems off (too high / too low)', value: 'disqualification_rate' },
  { label: 'Other reason, not listed', value: 'other' },
];

interface PauseModalProps {
  id: number;
  onClose: () => void;
  onSubmit: () => void;
}

const PauseModal = ({ id, onClose, onSubmit }: PauseModalProps) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<typeof possibleReasons>([]);

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);

    try {
      await pauseSurvey({
        id,
        reasons: selectedReasons.map((reason) => reason.label),
      });
      onSubmit();
    } catch (error) {
      const defaultErrorMessage = 'An unexpected error occurred. Please try again.';
      if (axios.isAxiosError(error) && error.response) {
        setError(
          error?.response.data?.detail || error?.response.data?.error || defaultErrorMessage
        );
      } else {
        setError(defaultErrorMessage);
      }
      setIsLoading(false);
    }
  };

  return (
    <Modal
      buttons={[
        <Button key="cancel-btn" onClick={onClose} text="Cancel" variant="secondary" />,
        <Button
          isLoading={isLoading}
          key="proceed-btn"
          onClick={handleSubmit}
          text="Proceed"
          variant="primary"
        />,
      ]}
      isActive
      onClose={onClose}
      title="Pause Survey"
    >
      {error && <Announcement text={error} variant="error" />}

      <p>Proceeding will stop this survey from collecting more responses.</p>
      <br />
      <Select
        data-testid="pause-reason"
        isMulti
        label="Reason for pausing"
        // @ts-ignore
        onChange={(values) => setSelectedReasons(values ?? [])}
        options={possibleReasons}
        placeholder="Select a reason"
        value={selectedReasons}
      />
      <br />
      <p>Our support team will reach out to you to determine next steps.</p>
    </Modal>
  );
};

export default PauseModal;
